// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { COLOURS  } from '../constants/constants';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface ChartProps {
  deploymentNames: string[];
  data: { [key: string]: number };
}

const CSPieChart: React.FC<ChartProps> = ({ deploymentNames, data }) => {

  const deploymentCount = deploymentNames.reduce(
    (acc: { [key: string]: number }, deployment: string) => {
      acc[deployment] = data[deployment] || 0; 
      return acc;
    },
    {}
  );

  const colours = COLOURS.slice(0, deploymentNames.length);

  const chartData = {
    labels: deploymentNames,
    datasets: [
      {
        label: 'Cases by Deployment',
        data: deploymentNames.map(name => deploymentCount[name] || 0), 
        backgroundColor: colours,
        borderColor: '#fff',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          generateLabels: (chart: ChartJS) => {
            const data = chart.data;
            if (data.labels && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                const backgroundColor = Array.isArray(data.datasets[0]?.backgroundColor)
                  ? data.datasets[0]?.backgroundColor[i]
                  : '#000';
                return {
                  text: `${label} (${value})`,
                  fillStyle: backgroundColor,
                  strokeStyle: backgroundColor,
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            return `${context.label}: ${value} cases`;
          },
        },
      },
      title: {
        display: true,
        text: 'Cases by Deployment',
        font: { size: 16 },
        align: "start" as const,
      },
    },
  };

  if (deploymentNames.length === 0 || Object.keys(data).length === 0) {
    return <p style={{ textAlign: 'center' }}>No data available for deployment cases.</p>;
  }

  return (
    <div style={{ width: '100%', height: '250px' }}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

export default CSPieChart;
